import { getActiveAccountId, setActiveAccountId, } from '#pplx/frontend/browser-storage/activeAccount.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { getCookie, removeCookie, setCookie } from '#pplx/frontend/cookies/provider.js';
export const LAST_ACTIVE_ACCOUNT_STORAGE_KEY = 'pplx-last-active-account';
export const LAST_ACTIVE_ACCOUNT_COOKIE_NAME = '__Host-pplx-last-active-account';
const LAST_ACTIVE_ACCOUNT_COOKIE_EXPIRY_DAYS = 400;
export function isDocumentInForeground(doc) {
    return doc.visibilityState === 'visible' && doc.hasFocus();
}
function writeLastActiveAccountCookie(accountId) {
    setCookie(LAST_ACTIVE_ACCOUNT_COOKIE_NAME, accountId, {
        expires: LAST_ACTIVE_ACCOUNT_COOKIE_EXPIRY_DAYS,
        path: '/',
        sameSite: 'Lax',
        secure: true,
    });
}
function clearLastActiveAccountCookie() {
    removeCookie(LAST_ACTIVE_ACCOUNT_COOKIE_NAME, { path: '/', secure: true });
}
/**
 * "Last visible account" — a single, browser-wide default seed for new tabs (RFC §7.1).
 *
 * `sessionStorage` (the per-tab active account) is empty in a brand-new tab, so a cmd-click
 * / "open in new tab" from a secondary-account tab would otherwise default to the primary.
 * We keep one shared localStorage value that is **written often, read once**: written
 * whenever a tab becomes visible/focused (so it tracks the tab you're clicking from), and
 * read only at a new tab's bootstrap to seed that tab. It is never re-read into an already
 * initialized tab, so it can't make two open tabs fight over an account — it's only a
 * default seed for fresh tabs.
 *
 * It is intentionally `{ global: true }`: a device-level value shared across accounts, not
 * per-account data. Dormant until something sets an active account (switcher / activation):
 * with no active account it's never written, so the seed read is a no-op.
 */
/** Write-often: record this tab's active account as the cross-tab default seed. */
export function recordLastActiveAccount() {
    const accountId = getActiveAccountId();
    if (accountId) {
        SafeLocalStorage.setItem(LAST_ACTIVE_ACCOUNT_STORAGE_KEY, accountId, { global: true });
        // __Host- requires Secure, Path=/, and no Domain, so a sibling subdomain cannot
        // shadow this selector on the consumer host.
        writeLastActiveAccountCookie(accountId);
    }
}
/**
 * Unconditionally clears the cross-tab seed — called on full sign-out, where every account is
 * gone, so the next bootstrap's `applyAccountFromLastActive` can't re-seed a now-cookieless
 * account into a fresh sign-in. Use `clearLastActiveAccountIfMatches` for single-account sign-out.
 */
export function clearLastActiveAccount() {
    SafeLocalStorage.removeItem(LAST_ACTIVE_ACCOUNT_STORAGE_KEY, { global: true });
    clearLastActiveAccountCookie();
}
/** Clear the cross-tab seed only when it belongs to the failed or signed-out account. */
export function clearLastActiveAccountIfMatches(accountId) {
    if (SafeLocalStorage.getItem(LAST_ACTIVE_ACCOUNT_STORAGE_KEY, { global: true }) === accountId) {
        SafeLocalStorage.removeItem(LAST_ACTIVE_ACCOUNT_STORAGE_KEY, { global: true });
    }
    if (getCookie(LAST_ACTIVE_ACCOUNT_COOKIE_NAME) === accountId) {
        clearLastActiveAccountCookie();
    }
}
/**
 * Read-once at a new tab's bootstrap: seed this tab's active account from the last-visible
 * default — but only when nothing more specific already set it (the `?pplx_account=` param
 * or an existing per-tab `sessionStorage` value, e.g. on refresh, both take precedence).
 */
export function applyAccountFromLastActive() {
    if (getActiveAccountId()) {
        return;
    }
    const seed = SafeLocalStorage.getItem(LAST_ACTIVE_ACCOUNT_STORAGE_KEY, { global: true });
    if (seed && validateUuid(seed)) {
        setActiveAccountId(seed);
    }
}
/** Install the write-often listeners without letting an unfocused window steal the shared seed. */
export function installLastActiveAccountTracking() {
    const recordForegroundAccount = () => {
        if (isDocumentInForeground(document)) {
            recordLastActiveAccount();
        }
    };
    document.addEventListener('visibilitychange', recordForegroundAccount);
    window.addEventListener('focus', recordForegroundAccount);
    recordForegroundAccount();
}
//# sourceMappingURL=lastActiveAccount.js.map